import React, { useEffect, useState } from 'react';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AddonsListProps,
  CustomizationData,
  OrderProductCardProps,
  ProductCustomization,
  ProductDetails,
} from '@helpers/Models';
import { OrderProductAddon } from '@components/BottomSheet/OrderProductAddon';
import { useToast } from '@contexts/Toast';
import {
  errorMessage,
  formRegex,
  ProductType,
  responseTexts,
  WeightType,
} from '@helpers/Constants';
import { Axios } from '@helpers/ApiService';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { faRepeat } from '@fortawesome/free-solid-svg-icons';
import { validateMinimumRequiredValue } from '@services/OrderCreationService';

export const ProductCard: React.FC<OrderProductCardProps> = ({
  isNext,
  products,
  masterProducts,
  onUpdate,
  handleRepeatCustomization,
  handleDeleteProduct,
}) => {
  console.log(
    'ProductCard props - products:',
    products?.length || 0,
    'masterProducts:',
    masterProducts?.length || 0,
    'isNext:',
    isNext,
  );

  const [showAddon, setShowAddon] = useState(false);
  const { showToastMessage } = useToast();
  const [activeProductIndex, setActiveProductIndex] = useState<number | null>(
    null,
  );
  const [addons, setAddons] = useState<AddonsListProps[]>([]);
  const [currentProduct, setCurrentProduct] = useState<ProductDetails | null>(
    null,
  );
  const [productCustomizations, setProductCustomizations] = useState<
    ProductCustomization[]
  >([]);
  const [isUpdatingFromProps, setIsUpdatingFromProps] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * On component mount, fetches the available addons.
   *
   * Runs only once when the component mounts.
   */
  useEffect(() => {
    fetchAddons();
  }, []);

  /**
   * Updates the local product customizations state whenever the `products` prop changes.
   * This ensures the component stays in sync with parent state changes (like cancel operations).
   */
  useEffect(() => {
    console.log(
      'ProductCard - Props changed, updating internal state:',
      products?.length || 0,
    );
    setIsUpdatingFromProps(true);
    setIsInitialized(true);
    setProductCustomizations(
      (products || []).map((item) => ({
        ...item,
        upchargeIngredientsFlag: item.upchargeIngredientsFlag ?? 0,
      })),
    );
    // Reset flag after state update
    setTimeout(() => setIsUpdatingFromProps(false), 0);
  }, [products]);

  /**
   * Calls the `onUpdate` callback prop whenever `productCustomizations` state changes,
   * but only when the update didn't originate from props (to prevent circular updates during cancel operations).
   *
   * This keeps the parent component synchronized with the current product customizations.
   */
  useEffect(() => {
    console.log(
      'ProductCard onUpdate check - isUpdatingFromProps:',
      isUpdatingFromProps,
      'isInitialized:',
      isInitialized,
      'products:',
      productCustomizations.length,
    );
    if (!isUpdatingFromProps && isInitialized) {
      console.log(
        'ProductCard calling onUpdate with products:',
        productCustomizations.length,
      );
      onUpdate(productCustomizations);
    }
  }, [productCustomizations, isUpdatingFromProps, isInitialized]);

  /**
   * Fetches the list of available add-ons from the API and updates the local state.
   *
   * Sends a POST request to the configured `FETCH_ADDONS` endpoint.
   * On success, updates the `addons` state with the fetched add-ons data.
   * On failure, displays an error toast message and throws the error.
   *
   * @returns {Promise<void>} A promise that resolves when the fetch is complete.
   */
  const fetchAddons = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ADDONS, {});
      setAddons(response.data.data.addOns);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Handles updates to individual product fields (e.g., quantity, fat %, add-ons)
   * within the ProductCard component.
   *
   * This function updates the local product customizations state and also notifies
   * the parent component of the changes via the `onUpdate` callback.
   *
   * @param {ProductCustomization[]} updated - The updated list of product customizations
   *   reflecting the changes made to one or more fields.
   */
  const handleUpdates = (updated: ProductCustomization[]) => {
    setProductCustomizations(updated);
    onUpdate(updated);
  };

  /**
   * Increments the quantity of a product at the specified index.
   *
   * This function updates the local `productCustomizations` state by increasing
   * the `quantity` of the product at the given index by 1. After updating the
   * local state, it calls `handleUpdates` to propagate the change to the parent
   * component via the `onUpdate` callback.
   *
   * @param {number} index - The index of the product to increment in the `productCustomizations` array.
   */
  const handleIncrement = (index: number): void => {
    const updated = productCustomizations.map((product, i) =>
      i === index
        ? { ...product, quantity: Number(product.quantity) + 1 }
        : product,
    );
    handleUpdates(updated);
  };

  /**
   * Decreases the quantity of a product by one while ensuring it doesn't go below the minimum allowed value.
   *
   * This function retrieves the target product from the `productCustomizations` list using the provided index,
   * decrements its quantity by 1, and validates the new value using `validateMinimumRequiredValue`.
   * If the new value passes validation, it updates the product list through `handleUpdates`.
   * Otherwise, it triggers an error toast message (via `validateMinimumRequiredValue`).
   *
   * @param {number} index - The index of the product to decrement in the `productCustomizations` array.
   *
   * @returns {void}
   *
   * @remarks
   * - Prevents quantity updates if the product doesn't exist at the given index.
   * - Uses `validateMinimumRequiredValue` to ensure the new quantity is not less than the allowed minimum.
   * - If validation succeeds, constructs a new updated products array and triggers `handleUpdates`.
   * - If validation fails, no update occurs (an error toast is shown instead).
   */
  const handleDecrement = (index: number): void => {
    // Get the current product
    const currentProduct = productCustomizations[index];
    if (!currentProduct) return;

    // Decrease the value by 1
    const currentValue = Number(currentProduct.quantity) - 1;

    // Validation check, returns boolean
    const isValid = validateMinimumRequiredValue(
      masterProducts,
      productCustomizations,
      index,
      currentValue,
      showToastMessage,
    );

    // If the input passes the validation, update the input with the product
    if (isValid) {
      const updatedProduct = productCustomizations.map((product, i) =>
        i === index ? { ...product, quantity: currentValue } : product,
      );
      handleUpdates(updatedProduct);
    }
  };

  /**
   * Opens the addon customization UI for the selected product at the given index.
   *
   * - Finds the corresponding master product details based on the product ID.
   * - Sets the current product details in state.
   * - Sets the active product index in state.
   * - Checks if the product has any existing customization.
   * - If customization exists, opens the customizing sheet UI.
   * - Otherwise, opens the addon selection UI.
   *
   * @param {number} index - The index of the product in the products array to customize.
   */
  const handleOpenAddon = (index: number) => {
    const productEntry = products?.[index];
    if (!productEntry) return;
    const master = masterProducts.find(
      (mp) => mp.id === productEntry.productId,
    );
    if (!master) return;

    console.log('master', master);
    const productDetails: ProductDetails = {
      id: master.id,
      name: master.name,
      price: master.price,
      minType: master.minType,
      minQuantity: master.minQuantity,
      upchargeIngredientsFlag: master.upchargeIngredientsFlag,
      allowFatContentFlag: master.allowFatContentFlag,
      productType: master.productType,
      weight: master.weight,
      allowAddOnFlag: master.allowAddOnFlag,
    };

    setCurrentProduct(productDetails);
    setActiveProductIndex(index);

    const hasCustomization = products?.some(
      (custom) => custom.productId === master.id,
    );
    // open add-ons customizing sheet
    if (hasCustomization) {
      setShowAddon(true);
    }
  };

  /**
   * Handles closing the addon selection UI and updates product customizations.
   *
   * - Updates the product customizations state with provided data or clears it if no data.
   * - Hides the addon selection UI.
   * - Opens the customizing sheet UI.
   *
   * @param {CustomizationData[] | null} data - The updated customization data, or null to clear.
   */
  const handleCloseAddon = (data: CustomizationData[] | null): void => {
    if (data) {
      setProductCustomizations(
        data.map((item) => ({
          ...item,
          upchargeIngredientsFlag: item.upchargeIngredientsFlag ?? 0,
        })),
      );
    } else {
      setProductCustomizations([]);
    }
    setShowAddon(false);
  };

  /**
   * Removes a specific add-on ID from a product customization at a given index.
   *
   * This function updates the `productCustomizations` state by removing the
   * add-on at `addonIndex` from the `addOnIds` array of the customization at `index`.
   * After successful removal, it shows a success toast notification.
   *
   * @param {number} addonIndex - The index of the add-on to remove from the customization's `addOnIds` array.
   * @param {number} index - The index of the customization in the `productCustomizations` array.
   *
   * @returns {void}
   */
  const handleDeleteAddOn = (addonIndex: number, index: number): void => {
    setProductCustomizations((prev) => {
      const updated = [...prev];
      const customization = updated[index];

      // Defensive check
      if (!customization) return prev;

      const newAddOnIds = customization.addOnIds.filter(
        (_, idx) => idx !== addonIndex,
      );

      updated[index] = {
        ...customization,
        addOnIds: newAddOnIds,
      };

      return updated;
    });

    showToastMessage(responseTexts.ADDON_REMOVE_SUCCESS, 'success');
  };

  /**
   * Handles the blur (focus loss) event for a product's measurement input field (quantity or weight).
   *
   * This function validates the product's current measurement value when the input loses focus.
   * If the entered value is below the minimum allowed, it resets the value to the product's minimum requirement
   * (based on its type: quantity or weight) and updates the product list.
   *
   * @param {number} index - The index of the product in the `productCustomizations` array whose measurement field lost focus.
   *
   * @returns {void}
   *
   * @remarks
   * - Uses `validateMinimumRequiredValue` to check whether the current measurement meets the minimum criteria.
   * - If the value is invalid:
   *   - Finds the corresponding master product.
   *   - Determines the minimum allowed value based on product type:
   *     - `ProductType.QUANTITY` → uses `masterProduct.minQuantity` (default: 1)
   *     - `ProductType.WEIGHT` → uses `masterProduct.weight` (default: 1)
   *   - Resets the product's quantity to the minimum value and calls `handleUpdates` to apply changes.
   * - If the product or master product is missing, the function safely exits without errors.
   */
  const handleMeasurementBlur = (index: number): void => {
    // Get the current product
    const product = productCustomizations[index];
    if (!product) return;

    // Validation check
    const isValid = validateMinimumRequiredValue(
      masterProducts,
      productCustomizations,
      index,
      Number(product.quantity),
      showToastMessage,
    );

    // If input is not valid, reset it to the minimum required value
    if (!isValid) {
      const masterProduct = masterProducts.find(
        (masterData) => masterData.id === product.productId,
      );
      if (!masterProduct) return; // Fallback
      // Get the minimum value based on the product type
      const minValue =
        masterProduct.productType === ProductType.QUANTITY
          ? masterProduct.minQuantity
          : // Set weight to 1 lb by default if it is 0.
            masterProduct.weight === 0
            ? 1
            : masterProduct.weight;

      // Update the final value
      const updatedProduct = productCustomizations.map((p, i) =>
        i === index ? { ...p, quantity: minValue } : p,
      );
      handleUpdates(updatedProduct);
    }
  };

  /**
   * Handles user input changes for a product's measurement field (quantity or weight).
   *
   * This function validates the entered measurement value based on the product type:
   * - For quantity-based products, it allows only whole numbers.
   * - For weight-based products, it allows decimal values with up to two decimal places.
   *
   * If the input passes validation, the function updates the corresponding product's quantity
   * in the `productCustomizations` list and triggers `handleUpdates` to apply the changes.
   *
   * @param {number} index - The index of the product in the `productCustomizations` array to update.
   * @param {string} value - The new input value entered by the user for the measurement field.
   * @param {number} productType - The type of product (e.g., `ProductType.QUANTITY` or `ProductType.WEIGHT`), which determines the validation rule.
   *
   * @returns {void}
   *
   * @remarks
   * - Uses `formRegex.NUMBERS` for validating quantity-based input.
   * - Uses `formRegex.DECIMAL_UP_TO_TWO_PLACES` for validating weight-based input.
   * - If validation fails, the function exits without updating the state.
   * - Prevents invalid entries (like letters, symbols, or excessive decimals) from being applied.
   */
  const handleMeasurementChange = (
    index: number,
    value: string,
    productType: number,
  ): void => {
    // Allow empty input for user clearing the field
    if (value === '') {
      const updatedProducts = productCustomizations.map((product, i) =>
        i === index ? { ...product, quantity: '' } : product,
      );
      handleUpdates(updatedProducts);
      return;
    }

    // Select the regex based on product type
    const isValid =
      productType === ProductType.QUANTITY
        ? formRegex.NUMBERS.test(value)
        : formRegex.DECIMAL_UP_TO_TWO_PLACES.test(value);

    // If validation fails, return
    if (!isValid) return;

    // Update only if the validation passes
    const updatedProducts = productCustomizations.map((product, i) =>
      // Converting value to Number before updating, as String type is used for regex validation
      i === index ? { ...product, quantity: value } : product,
    );
    handleUpdates(updatedProducts);
  };

  return (
    <div className="w-full mx-auto m-4">
      {productCustomizations.length > 0 &&
        productCustomizations.map((product, index) => {
          // console.log('product', product);
          const masterProduct = masterProducts.find(
            (mp) => mp.id === product.productId,
          );
          const addonsForProduct = product.addOnIds
            .map((id) => addons.find((addon) => addon.id === id))
            .filter((addon): addon is AddonsListProps => addon !== undefined);
          return (
            <div
              key={index}
              className="p-3 bg-[#FFFEFA] border mb-4 rounded-md shadow-sm"
            >
              {/* product name and quantity card */}
              <div className="flex justify-between items-center">
                <h1 className="text-lg text-gray-800 mb-4 font-medium">
                  {masterProduct?.name}
                  {product.fatPercent > 0 && `, ${product.fatPercent}% Fat`}
                </h1>
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-gray-600 font-semibold mb-2 mt-2 mr-4 text-lg">
                    {masterProduct?.productType == 1 ? 'Weight: ' : 'Quantity:'}
                  </p>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => handleDecrement(index)}
                      className="bg-primary text-white font-bold py-1 px-3 rounded-l"
                    >
                      -
                    </button>
                    <input
                      type="text"
                      inputMode="decimal" // For displaying decimal keyboard in iOS
                      value={product.quantity ?? ''}
                      onBlur={() => handleMeasurementBlur(index)}
                      onChange={(e) =>
                        handleMeasurementChange(
                          index,
                          e.target.value,
                          masterProduct?.productType ?? 1, // Fallback
                        )
                      }
                      className="w-20 h-8 text-center border-t border-b focus:outline-primary"
                    />
                    <button
                      type="button"
                      onClick={() => handleIncrement(index)}
                      className="bg-primary text-white font-bold py-1 px-3 rounded-r"
                    >
                      +
                    </button>
                    <span className="ml-2 text-gray-500">
                      {masterProduct?.productType == 1 && WeightType.LBS}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mb-6 flex items-center justify-between px-4 py-2 rounded-md bg-quaternary">
                <div>
                  <span className="text-gray-600">Price: </span>
                  <span className="text-gray-800 font-semibold">
                    {/* Display Item as mintype for product type quantity and lbs for weight */}
                    ${masterProduct?.price}/
                    {masterProduct?.productType === ProductType.QUANTITY
                      ? 'Item'
                      : `${WeightType.LBS} `}
                  </span>
                </div>
                <div className="whitespace-nowrap">
                  <span className="text-gray-600 mt-2">
                    Total Estimated weight in Lbs:{' '}
                  </span>
                  <span className="text-gray-800 font-semibold">
                    {Number(
                      masterProduct?.productType === ProductType.QUANTITY
                        ? (Number(product?.quantity) ?? 0) *
                            (masterProduct?.weight ?? 0)
                        : (product?.quantity ?? 0),
                    ).toFixed(2)}{' '}
                    {WeightType.LBS}
                  </span>
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Add-ons
                {masterProduct?.upchargeIngredientsFlag === 0 ? (
                  <span className="ml-2">(No Up-Charge)</span>
                ) : null}
              </h2>
              {addonsForProduct.length > 0 ? (
                addonsForProduct.map((addon, addonIndex) => (
                  <div
                    className="flex items-center justify-between mb-4 bg-gray-50 rounded"
                    key={addon.id}
                  >
                    <div className="flex items-center w-full">
                      <div className="relative w-full pr-4">
                        <button
                          type="button"
                          className="w-full flex items-center justify-between border border-gray-300 rounded px-3 py-1 mr-2"
                        >
                          <span className="text-gray-800 mr-4">
                            {addon.name}
                          </span>
                          <span className="flex items-center">
                            <span>
                              {/* + ${addon.price} / lbs */}
                              {masterProduct!.upchargeIngredientsFlag === 0
                                ? '$0'
                                : `$${addon.price?.toFixed(2)} `}
                              /{WeightType.LBS}
                              {/* {' '}
                              {addon?. ?? 'unit'} */}
                            </span>
                            <svg
                              className="w-4 h-4 ml-1 transition-transform"
                              fill="none"
                              stroke="#A09CAB"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </span>
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteAddOn(addonIndex, index)}
                      type="button"
                      className="text-[#4D545C]-500 hover:text-[#4D545C]-700 border border-gray-400 rounded px-2 py-1"
                    >
                      <FontAwesomeIcon
                        icon={faTrashCan}
                        className="text-[#4D545C]"
                      />
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-gray-500">
                  No add-ons selected for this product.
                </p>
              )}
              {showAddon && activeProductIndex !== null && (
                <OrderProductAddon
                  onClose={handleCloseAddon}
                  selectedCustomization={productCustomizations}
                  product={currentProduct!}
                  currentProductIndex={activeProductIndex}
                />
              )}
              {isNext ? (
                <div className="flex items-justify space-x-3 mt-4">
                  <div className="w-1/2">
                    <button
                      onClick={() => handleRepeatCustomization(product, index)}
                      type="button"
                      className="w-full px-6 py-1.5 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary flex items-center justify-center gap-2"
                    >
                      <FontAwesomeIcon icon={faRepeat} />
                      Duplicate {/* Previously Repeat Customization */}
                    </button>
                  </div>
                  <div className="w-1/2">
                    <button
                      onClick={() => handleDeleteProduct(index)}
                      type="button"
                      className="w-full px-6 py-1.5 border rounded-sm text-sm font-medium text-red-600 bg-white border-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => handleOpenAddon(index)}
                  type="button"
                  className="w-full border border-primary px-6 py-1 bg-white rounded text-primary font-semibold"
                >
                  Add More Add-Ons
                </button>
              )}
            </div>
          );
        })}
    </div>
  );
};
